/**
 * ActionPanel — Activity icon dropdown (action inbox).
 *
 * Shows intake.needs_review and intake.needs_clarification items.
 * Opening an item marks it as read (not actioned — user must click a CTA).
 * Clicking "Review in CF" or "Open in ADO" marks it as actioned.
 */

import {
  dismissOne as apiDismissOne,
  markActioned as apiMarkActioned,
  markRead as apiMarkRead,
  updateArafBearerToken,
} from '@avila-roffe/api-clients-araf-factory';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@avila-roffe/components-web';
import { Activity, ExternalLink, X } from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../../stores/authStore';
import { selectUnactionedCount, useNotificationStore } from '../../stores/notificationStore';
import type { AppNotification } from '../../types/notifications';
import { formatRelativeTime, routeLabel } from './notification-utils';

// ─── Event type display ───────────────────────────────────────────────────────

function eventIcon(eventType: AppNotification['eventType']): string {
  switch (eventType) {
    case 'intake.needs_review':
      return '📋';
    case 'intake.needs_clarification':
      return '⚠️';
    default:
      return '❓';
  }
}

function actionLabel(eventType: AppNotification['eventType']): string {
  switch (eventType) {
    case 'intake.needs_review':
      return 'Ready for review';
    case 'intake.needs_clarification':
      return 'Needs clarification';
    default:
      return 'Action required';
  }
}

// ─── Single action row ────────────────────────────────────────────────────────

interface ActionRowProps {
  notification: AppNotification;
  onSelect: (n: AppNotification) => void;
  onDismiss: (id: string) => void;
}

function ActionRow({ notification: n, onSelect, onDismiss }: ActionRowProps) {
  return (
    <button
      type="button"
      className={`group relative flex items-start gap-3 px-4 py-3 w-full text-left cursor-pointer hover:bg-muted/50 transition-colors ${
        !n.actioned ? 'bg-amber-500/5' : ''
      }`}
      onClick={() => onSelect(n)}
    >
      {/* Unactioned indicator */}
      {!n.actioned && (
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-amber-500 shrink-0" />
      )}

      <span className="text-base leading-none mt-0.5 shrink-0">{eventIcon(n.eventType)}</span>

      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-snug truncate ${!n.actioned ? 'font-medium' : 'text-muted-foreground'}`}
        >
          {n.workItemTitle}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {routeLabel(n.route)} · {formatRelativeTime(n.createdAt)}
        </p>
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 font-medium">
          {actionLabel(n.eventType)}
        </p>
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(n.id);
        }}
      >
        <X className="size-3.5" />
      </button>
    </button>
  );
}

// ─── Detail sheet ─────────────────────────────────────────────────────────────

interface DetailSheetProps {
  notification: AppNotification | null;
  open: boolean;
  onClose: () => void;
  onActioned: (id: string) => void;
}

function DetailSheet({ notification: n, open, onClose, onActioned }: DetailSheetProps) {
  if (!n) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <span>{eventIcon(n.eventType)}</span>
            <span>
              #{n.workItemId} — {n.workItemTitle}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{routeLabel(n.route)}</Badge>
            <Badge variant="warning">{actionLabel(n.eventType)}</Badge>
          </div>

          {n.detail && <p className="text-sm text-muted-foreground leading-relaxed">{n.detail}</p>}

          <p className="text-xs text-muted-foreground">{new Date(n.createdAt).toLocaleString()}</p>

          <p className="text-xs text-muted-foreground italic">
            Opening this item does not mark it as actioned — click a button below to take action.
          </p>

          <div className="flex gap-2 pt-2">
            {n.cfUrl && (
              <Button
                size="sm"
                onClick={() => {
                  window.open(n.cfUrl, '_blank');
                  onActioned(n.id);
                  onClose();
                }}
                className="flex items-center gap-1.5"
              >
                Review in Code Factory
                <ExternalLink className="size-3" />
              </Button>
            )}
            {n.adoUrl && (
              <Button
                appearance="outline"
                size="sm"
                onClick={() => {
                  window.open(n.adoUrl, '_blank');
                  onActioned(n.id);
                  onClose();
                }}
                className="flex items-center gap-1.5"
              >
                Open in ADO
                <ExternalLink className="size-3" />
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── ActionPanel ──────────────────────────────────────────────────────────────

export function ActionPanel() {
  const token = useAuthStore((state) => state.accessToken);

  const actions = useNotificationStore(
    useShallow((s) => s.notifications.filter((n) => n.category === 'action')),
  );
  const unactionedCount = useNotificationStore(selectUnactionedCount);
  const markRead = useNotificationStore((s) => s.markRead);
  const markActioned = useNotificationStore((s) => s.markActioned);
  const removeOne = useNotificationStore((s) => s.removeOne);

  const [open, setOpen] = useState(false);
  const [detailNotification, setDetailNotification] = useState<AppNotification | null>(null);

  const handleSelect = (n: AppNotification) => {
    setDetailNotification(n);
    // Opening = read only, not actioned
    if (!n.read) {
      markRead(n.id);
      if (token) {
        updateArafBearerToken(token);
        apiMarkRead(n.id, { recipient: '' }).catch(() => {});
      }
    }
  };

  const handleActioned = (id: string) => {
    markActioned(id);
    if (token) {
      updateArafBearerToken(token);
      apiMarkActioned(id, { recipient: '' }).catch(() => {});
    }
  };

  const handleDismiss = (id: string) => {
    removeOne(id);
    if (token) {
      updateArafBearerToken(token);
      apiDismissOne(id, { recipient: '' }).catch(() => {});
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            appearance="ghost"
            size="icon"
            aria-label="Pending actions"
            className="relative text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
          >
            <Activity className="size-4" />
            {unactionedCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-medium leading-none">
                {unactionedCount > 9 ? '9+' : unactionedCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-80 p-0" aria-label="Pending actions">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-semibold">Pending Actions</span>
            {unactionedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {unactionedCount} pending
              </Badge>
            )}
          </div>

          <Separator />

          {/* List */}
          {actions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
              <Activity className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No pending actions</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="divide-y divide-border">
                {actions.map((n) => (
                  <ActionRow
                    key={n.id}
                    notification={n}
                    onSelect={(notif) => {
                      setOpen(false);
                      handleSelect(notif);
                    }}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </PopoverContent>
      </Popover>

      <DetailSheet
        notification={detailNotification}
        open={detailNotification !== null}
        onClose={() => setDetailNotification(null)}
        onActioned={handleActioned}
      />
    </>
  );
}
